import React from 'react';
import styled from 'styled-components';
import DashboardFeature from './DashboardFeature';
import { FaYoutube, FaSpotify, FaAmazon, FaApple } from 'react-icons/fa';
import { SiNetflix, SiGoogleplay } from 'react-icons/si';

const FeaturesSection = styled.section`
  padding: 120px 0;
  background: var(--gradient-surface);
  position: relative;
  overflow: hidden;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 1200px;
    height: 1200px;
    border-radius: 50%;
    background: var(--gradient-primary);
    opacity: 0.05;
  }

  &::before {
    top: -600px;
    right: -300px;
    animation: float 8s ease-in-out infinite;
  }

  &::after {
    bottom: -600px;
    left: -300px;
    animation: float 8s ease-in-out infinite reverse;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-30px) rotate(5deg); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: var(--text-secondary);
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 600;
  text-align: center;
  animation: fadeIn var(--transition-normal) forwards;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: var(--gradient-primary);
    border-radius: 2px;
  }
`;

const MainTitle = styled.h3`
  font-size: 48px;
  font-weight: 800;
  margin-bottom: 60px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
  text-align: center;
  animation: slideIn var(--transition-normal) forwards;
  text-shadow: 0 2px 10px rgba(99, 102, 241, 0.1);
`;

const ServiceIconsWrapper = styled.div`
  position: relative;
  padding: 60px 0;
  margin-bottom: 80px;
  background: var(--surface);
  border-radius: 20px;
  box-shadow: var(--shadow-md);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    border-radius: 4px 4px 0 0;
  }
`;

const SectionSubtitle = styled.h2`
  font-size: 28px;
  color: var(--text-secondary);
  margin-bottom: 40px;
  font-weight: 600;
  text-align: center;
  animation: fadeIn var(--transition-normal) forwards;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ServiceIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
  animation: fadeIn var(--transition-normal) forwards;
  padding: 0 20px;
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--surface);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-md);
  position: relative;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: var(--shadow-primary);
    background: var(--gradient-primary);
    
    svg {
      color: white;
      transform: scale(1.2) rotate(5deg);
    }

    &::after {
      opacity: 1;
      transform: translateY(0);
    }
  }

  svg {
    width: 40px;
    height: 40px;
    color: var(--primary);
    transition: all var(--transition-normal);
  }

  &::after {
    content: attr(title);
    position: absolute;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%) translateY(10px);
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    opacity: 0;
    transition: all var(--transition-normal);
    white-space: nowrap;
    background: var(--surface);
    padding: 4px 12px;
    border-radius: 6px;
    box-shadow: var(--shadow-sm);
  }
`;

const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  box-shadow: var(--shadow-sm);
  margin-bottom: 20px;
  transition: all var(--transition-normal);

  img {
    width: 24px;
    height: 24px;
  }
`;

const FeatureCard = styled.div`
  padding: 32px;
  background: var(--surface);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  border: 1px solid var(--border);
  animation: fadeIn var(--transition-normal) forwards;
  animation-delay: ${props => props.index * 0.1}s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);

    ${FeatureIcon} {
      transform: scale(1.1) rotate(5deg);
      box-shadow: var(--shadow-md);
    }
  }
`;

const FeatureContent = styled.div`
  text-align: left;
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
`;

const FeatureText = styled.p`
  font-size: 16px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  animation: slideIn var(--transition-normal) forwards;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QuoteText = styled.p`
  font-size: 24px;
  line-height: 1.6;
  color: var(--text-primary);
  margin-bottom: 20px;
  position: relative;
  transition: all var(--transition-normal);
`;

const QuoteSection = styled.div`
  text-align: center;
  margin: 80px auto;
  max-width: 800px;
  padding: 40px;
  background: var(--surface);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  animation: fadeIn var(--transition-normal) forwards;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--gradient-primary);
    opacity: 0;
    transition: all var(--transition-normal);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);

    &::before {
      opacity: 0.05;
    }

    ${QuoteText} {
      transform: scale(1.02);
    }
  }
`;

const QuoteHighlight = styled.span`
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`;

const Features = () => {
  const services = [
    { icon: <FaYoutube />, name: 'YouTube' },
    { icon: <FaSpotify />, name: 'Spotify' },
    { icon: <SiNetflix />, name: 'Netflix' },
    { icon: <FaAmazon />, name: 'Amazon' },
    { icon: <FaApple />, name: 'Apple' },
    { icon: <SiGoogleplay />, name: 'Google Play' }
  ];

  return (
    <>
      <FeaturesSection>
        <Container>
          <ServiceIconsWrapper>
            <SectionSubtitle>
              All your service subscriptions managed in one app
            </SectionSubtitle>
            <ServiceIcons>
              {services.map((service, index) => (
                <ServiceIcon key={index} title={service.name}>
                  {service.icon}
                </ServiceIcon>
              ))}
            </ServiceIcons>
          </ServiceIconsWrapper>

          <SectionTitle>Why choose Nexpend?</SectionTitle>
          <MainTitle>
            Simplify Subscription Management<br />and Save Money
          </MainTitle>

          <FeaturesGrid>
            {[
              {
                icon: "/icons/cart.svg",
                title: "Track All Subscription Costs",
                text: "Get real-time insights into your total subscription spending, broken down by month or year."
              },
              {
                icon: "/icons/menu.svg",
                title: "Effortless Management",
                text: "Simplify subscription management with tags, filters, sorting, and a clean, simple dashboard."
              },
              {
                icon: "/icons/reminder.svg",
                title: "Smart Payment Reminders",
                text: "Never miss another payment with intelligent reminders and due date tracking."
              },
              {
                icon: "/icons/card.svg",
                title: "Payment Method Tracking",
                text: "Know exactly which card will be charged and when for each subscription."
              }
            ].map((feature, index) => (
              <FeatureCard key={index} index={index}>
                <FeatureIcon>
                  <img src={feature.icon} alt={feature.title} />
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureText>{feature.text}</FeatureText>
                </FeatureContent>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          <QuoteSection>
            <QuoteText>
              Tired of seeing <QuoteHighlight>5, 10 or even 20+</QuoteHighlight> recurring charges on your statements?
            </QuoteText>
            <QuoteText>
              Take control with <QuoteHighlight>Nexpend</QuoteHighlight>
            </QuoteText>
          </QuoteSection>
        </Container>
      </FeaturesSection>
      <DashboardFeature />
    </>
  );
};

const IconWrapper = styled.div`
  font-size: 48px;
  color: #000;
  margin-bottom: 20px;

  svg {
    width: 48px;
    height: 48px;
  }
`;

export default Features; 